import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  TableSortLabel,
  IconButton,
  Collapse,
} from "@mui/material";
import { AlertTypeEnum } from "../../features/alerts/AlertTypeEnum";
import { MilestoneTaskEstimateChangedAlert } from "../../features/alerts/MilestoneTaskEstimateChangedAlert";
import { ClockifyTimeEntryTimeChangedAlert } from "../../features/alerts/clockifyTimeChangedAlert/ClockifyTimeEntryTimeChangedAlert";
import { PercentageCompleteChangedAlert } from "../../features/alerts/percentageCompleteChangedAlert/PercentageCompleteChangedAlert";
import { ClockifyTimeEntry } from "../../features/clockifyTimeEntries/ClockifyTimeEntry";
import { MilestoneTask } from "../../features/taskReports/MilestoneTask";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AlertTypeContract } from "../../features/alerts/AlertTypeContract";
import {
  addClockifyTimeEntryTimeProcessedAlertIds,
  addMilestoneTaskEstimateProcessedAlertIds,
  addPercentageCompleteProcessedAlertIds,
  selectClockifyTimeEntryTimeChangedReadAlerts,
  selectMilestoneEstimateProcessedAlertIds,
  selectPercentageCompleteChangedReadAlertsIds,
} from "../../features/alerts/alertsSlice";
import { addAlertReadEventAsync } from "../../features/alerts/alertsApi";
import TimeFormatter from "../../features/extensions/TimeFormatter";
import { AlertSortField } from "./AlertSortField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type ProcessedAlert =
  | (MilestoneTaskEstimateChangedAlert & {
      type: AlertTypeEnum.MilestoneTaskEstimate;
    })
  | (ClockifyTimeEntryTimeChangedAlert & {
      type: AlertTypeEnum.TimeEntryTime;
    })
  | (PercentageCompleteChangedAlert & {
      type: AlertTypeEnum.PercentageComplete;
    });

const headCells = [
  {
    id: AlertSortField.TaskNumber,
    label: "Task Number",
    width: "8%",
    sortable: true,
  },
  {
    id: AlertSortField.Message,
    label: "Message",
    width: "40%",
    sortable: true,
  },
  {
    id: AlertSortField.Developers,
    label: "Developer(s)",
    width: "15%",
    sortable: true,
  },
  {
    id: AlertSortField.TotalHours,
    label: "Time Budget",
    width: "12%",
    sortable: true,
  },
  { id: AlertSortField.Date, label: "Date", width: "10%", sortable: true },
  { id: AlertSortField.Type, label: "Type", width: "15%", sortable: true },
];

const pageSizeOptions = [10, 20, 50, 100];

interface Alert {
  id: number;
  taskNumber: number;
  message: string;
  developers: string;
  timeBudget: string;
  date: string;
  type: AlertTypeEnum;
}

interface AlertsTableProps {
  milestoneTaskEstimateAlerts: MilestoneTaskEstimateChangedAlert[];
  clockifyTimeChangedAlerts: ClockifyTimeEntryTimeChangedAlert[];
  percentageCompleteChangedAlerts: PercentageCompleteChangedAlert[];
  timeEntries: ClockifyTimeEntry[];
  milestoneTasks: MilestoneTask[];
  loading?: boolean;
  initializationStatus: string;
  isExpanded?: boolean;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
}

export const AlertsTable = ({
  milestoneTaskEstimateAlerts,
  clockifyTimeChangedAlerts,
  percentageCompleteChangedAlerts,
  timeEntries,
  milestoneTasks,
  loading = false,
  initializationStatus,
  isExpanded = true,
  showExpandButton = false,
  onExpandClick,
}: AlertsTableProps) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [localSortConfig, setLocalSortConfig] = useState<{
    field: AlertSortField;
    direction: "asc" | "desc";
  }>({
    field: AlertSortField.Date,
    direction: "desc",
  });

  const clockifyTimeChangedReadAlerts = useAppSelector(
    selectClockifyTimeEntryTimeChangedReadAlerts
  );
  const milestoneTaskEstimateReadAlerts = useAppSelector(
    selectMilestoneEstimateProcessedAlertIds
  );
  const percentageCompleteReadAlertIds = useAppSelector(
    selectPercentageCompleteChangedReadAlertsIds
  );

  const processedAlertsRef = useRef<{
    clockify: number[];
    estimate: number[];
    percentage: number[];
  }>({
    clockify: [],
    estimate: [],
    percentage: [],
  });

  const allAlerts: ProcessedAlert[] = [
    ...milestoneTaskEstimateAlerts.map(
      (alert): ProcessedAlert => ({
        ...alert,
        type: AlertTypeEnum.MilestoneTaskEstimate,
      })
    ),
    ...clockifyTimeChangedAlerts.map(
      (alert): ProcessedAlert => ({
        ...alert,
        type: AlertTypeEnum.TimeEntryTime,
      })
    ),
    ...percentageCompleteChangedAlerts.map(
      (alert): ProcessedAlert => ({
        ...alert,
        type: AlertTypeEnum.PercentageComplete,
      })
    ),
  ];

  const totalPages = Math.ceil(allAlerts.length / pageSize);

  const timeFormatter = new TimeFormatter();

  const getAlertTypeLabel = (type: AlertTypeEnum) => {
    switch (type) {
      case AlertTypeEnum.MilestoneTaskEstimate:
        return "Task Estimate";
      case AlertTypeEnum.TimeEntryTime:
        return "Time Entry";
      case AlertTypeEnum.PercentageComplete:
        return "Percentage Complete";
      default:
        return "Unknown";
    }
  };

  const getAlertMessage = (alert: ProcessedAlert) => {
    switch (alert.type) {
      case AlertTypeEnum.MilestoneTaskEstimate:
        return `Task estimate changed from ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.MilestoneTaskEstimate }
          >
        ).from.toFixed(2)} hours to ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.MilestoneTaskEstimate }
          >
        ).to.toFixed(2)} hours`;
      case AlertTypeEnum.TimeEntryTime:
        return `Time entry changed from ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.TimeEntryTime }
          >
        ).from.toFixed(2)} hours to ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.TimeEntryTime }
          >
        ).to.toFixed(2)} hours`;
      case AlertTypeEnum.PercentageComplete:
        return `Percentage complete changed from ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.PercentageComplete }
          >
        ).from.toFixed(0)}% to ${(
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.PercentageComplete }
          >
        ).to.toFixed(0)}%`;
      default:
        return "Unknown alert type";
    }
  };

  const getTaskNumber = (alert: ProcessedAlert): string => {
    switch (alert.type) {
      case AlertTypeEnum.MilestoneTaskEstimate:
        return (
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.MilestoneTaskEstimate }
          >
        ).milestoneTask.gitLabTaskNumber.toString();
      case AlertTypeEnum.TimeEntryTime:
        const timeEntry = timeEntries.find(
          (entry: ClockifyTimeEntry) =>
            entry.id ===
            (
              alert as Extract<
                ProcessedAlert,
                { type: AlertTypeEnum.TimeEntryTime }
              >
            ).clockifyTimeEntryId
        );
        return timeEntry?.gitLabTaskId?.toString() || "N/A";
      case AlertTypeEnum.PercentageComplete:
        const task = milestoneTasks.find(
          (task) =>
            task.id ===
            (
              alert as Extract<
                ProcessedAlert,
                { type: AlertTypeEnum.PercentageComplete }
              >
            ).milestoneTaskId
        );
        return task?.gitLabTaskNumber?.toString() || "N/A";
      default:
        return "N/A";
    }
  };

  const getDevelopers = (alert: ProcessedAlert): string => {
    switch (alert.type) {
      case AlertTypeEnum.MilestoneTaskEstimate: {
        const task = (
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.MilestoneTaskEstimate }
          >
        ).milestoneTask;
        const developers = new Set(
          task.timeEntries.map((entry) => entry.developerName)
        );
        return Array.from(developers).join(", ") || "N/A";
      }
      case AlertTypeEnum.TimeEntryTime: {
        const timeEntry = timeEntries.find(
          (entry) =>
            entry.id ===
            (
              alert as Extract<
                ProcessedAlert,
                { type: AlertTypeEnum.TimeEntryTime }
              >
            ).clockifyTimeEntryId
        );
        return timeEntry?.developerName || "N/A";
      }
      case AlertTypeEnum.PercentageComplete: {
        const task = milestoneTasks.find(
          (task) =>
            task.id ===
            (
              alert as Extract<
                ProcessedAlert,
                { type: AlertTypeEnum.PercentageComplete }
              >
            ).milestoneTaskId
        );
        if (!task) return "N/A";
        const developers = new Set(
          task.timeEntries.map((entry) => entry.developerName)
        );
        return Array.from(developers).join(", ") || "N/A";
      }
      default:
        return "N/A";
    }
  };

  const getTimeBudget = (alert: ProcessedAlert): string => {
    switch (alert.type) {
      case AlertTypeEnum.MilestoneTaskEstimate: {
        const task = (
          alert as Extract<
            ProcessedAlert,
            { type: AlertTypeEnum.MilestoneTaskEstimate }
          >
        ).milestoneTask;
        return task.timeEstimate !== "N/A"
          ? task.timeEstimate.toString()
          : "N/A";
      }
      case AlertTypeEnum.TimeEntryTime:
        return "N/A"; // No time budget for this alert type
      case AlertTypeEnum.PercentageComplete: {
        const task = milestoneTasks.find(
          (task) =>
            task.id ===
            (
              alert as Extract<
                ProcessedAlert,
                { type: AlertTypeEnum.PercentageComplete }
              >
            ).milestoneTaskId
        );
        return task?.timeEstimate !== "N/A" && task?.timeEstimate !== undefined
          ? timeFormatter.toHoursAndMinutes(task.timeEstimate)
          : "N/A";
      }
      default:
        return "N/A";
    }
  };

  const mapToAlertObjects = (alerts: ProcessedAlert[]): Alert[] => {
    return alerts.map((alert) => ({
      id: alert.id,
      taskNumber: parseInt(getTaskNumber(alert)) || 0,
      message: getAlertMessage(alert),
      developers: getDevelopers(alert),
      timeBudget: getTimeBudget(alert),
      date: new Date(alert.dateCreated).toLocaleString(),
      type: alert.type,
    }));
  };

  const mappedAlerts: Alert[] = mapToAlertObjects(allAlerts);

  const sortAlertObjects = (alerts: Alert[]): Alert[] => {
    return [...alerts].sort((a, b) => {
      switch (localSortConfig.field) {
        case AlertSortField.TaskNumber:
          return localSortConfig.direction === "asc"
            ? a.taskNumber - b.taskNumber
            : b.taskNumber - a.taskNumber;
        case AlertSortField.Message:
          return localSortConfig.direction === "asc"
            ? a.message.localeCompare(b.message)
            : b.message.localeCompare(a.message);
        case AlertSortField.Developers:
          return localSortConfig.direction === "asc"
            ? a.developers.localeCompare(b.developers)
            : b.developers.localeCompare(a.developers);
        case AlertSortField.TotalHours:
          return localSortConfig.direction === "asc"
            ? a.timeBudget.localeCompare(b.timeBudget)
            : b.timeBudget.localeCompare(a.timeBudget);
        case AlertSortField.Date:
          return localSortConfig.direction === "asc"
            ? new Date(a.date).getTime() - new Date(b.date).getTime()
            : new Date(b.date).getTime() - new Date(a.date).getTime();
        case AlertSortField.Type:
          return localSortConfig.direction === "asc"
            ? a.type - b.type
            : b.type - a.type;
        default:
          return 0;
      }
    });
  };

  const sortedMappedAlerts = sortAlertObjects(mappedAlerts);

  const paginatedAlerts: ProcessedAlert[] = allAlerts.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  const paginatedMappedAlerts: Alert[] = sortedMappedAlerts.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handleSort = (field: AlertSortField) => {
    const direction =
      localSortConfig.field === field && localSortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setLocalSortConfig({ field, direction });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const sendProcessedAlerts = useCallback(() => {
    if (processedAlertsRef.current.clockify.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.clockify,
        alertType: AlertTypeContract.TimeEntryTime,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addClockifyTimeEntryTimeProcessedAlertIds(
          processedAlertsRef.current.clockify
        )
      );
    }

    if (processedAlertsRef.current.estimate.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.estimate,
        alertType: AlertTypeContract.MilestoneTaskEstimate,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addMilestoneTaskEstimateProcessedAlertIds(
          processedAlertsRef.current.estimate
        )
      );
    }

    if (processedAlertsRef.current.percentage.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.percentage,
        alertType: AlertTypeContract.PercentageComplete,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addPercentageCompleteProcessedAlertIds(
          processedAlertsRef.current.percentage
        )
      );
    }
  }, [dispatch]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && initializationStatus === "initialized") {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              const visibleTimeEntryIds = timeEntries.map((entry) => entry.id);
              const visibleTaskIds = milestoneTasks.map((task) => task.id);

              const visibleClockifyAlerts = paginatedAlerts.filter(
                (
                  alert
                ): alert is Extract<
                  ProcessedAlert,
                  { type: AlertTypeEnum.TimeEntryTime }
                > =>
                  alert.type === AlertTypeEnum.TimeEntryTime &&
                  visibleTimeEntryIds.includes(alert.clockifyTimeEntryId) &&
                  !clockifyTimeChangedReadAlerts.some(
                    (readAlert) => readAlert.id === alert.id
                  )
              );

              if (visibleClockifyAlerts.length > 0) {
                processedAlertsRef.current.clockify = [
                  ...processedAlertsRef.current.clockify,
                  ...visibleClockifyAlerts.map((alert) => alert.id),
                ];
              }

              const visibleTaskEstimateAlerts = paginatedAlerts.filter(
                (
                  alert
                ): alert is Extract<
                  ProcessedAlert,
                  { type: AlertTypeEnum.MilestoneTaskEstimate }
                > =>
                  alert.type === AlertTypeEnum.MilestoneTaskEstimate &&
                  visibleTaskIds.includes(alert.milestoneTaskId) &&
                  !milestoneTaskEstimateReadAlerts.includes(alert.id)
              );

              if (visibleTaskEstimateAlerts.length > 0) {
                processedAlertsRef.current.estimate = [
                  ...processedAlertsRef.current.estimate,
                  ...visibleTaskEstimateAlerts.map((alert) => alert.id),
                ];
              }

              const visiblePercentageAlerts = paginatedAlerts.filter(
                (
                  alert
                ): alert is Extract<
                  ProcessedAlert,
                  { type: AlertTypeEnum.PercentageComplete }
                > =>
                  alert.type === AlertTypeEnum.PercentageComplete &&
                  visibleTaskIds.includes(alert.milestoneTaskId) &&
                  !percentageCompleteReadAlertIds.includes(alert.id)
              );

              if (visiblePercentageAlerts.length > 0) {
                processedAlertsRef.current.percentage = [
                  ...processedAlertsRef.current.percentage,
                  ...visiblePercentageAlerts.map((alert) => alert.id),
                ];
              }

              sendProcessedAlerts();
            }, 1000);
          }
        });
      },
      { threshold: 0.5, rootMargin: "50px" }
    );

    const tableContainer = document.querySelector('[data-testid="alerts-table-container"]');
    if (tableContainer) {
      observer.observe(tableContainer);
    }

    return () => {
      if (tableContainer) {
        observer.unobserve(tableContainer);
      }
      clearTimeout(timeoutId);
    };
  }, [
    initializationStatus,
    timeEntries,
    milestoneTasks,
    paginatedAlerts,
    clockifyTimeChangedReadAlerts,
    milestoneTaskEstimateReadAlerts,
    percentageCompleteReadAlertIds,
    sendProcessedAlerts,
  ]);

  useEffect(() => {
    if (initializationStatus === "initialized") {
      sendProcessedAlerts();
    }
  }, [initializationStatus, page, pageSize, sendProcessedAlerts]);

  if (loading && !allAlerts.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allAlerts.length) {
    return (
      <Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 0.5 }}
        >
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Alerts
          </Typography>
        </Box>
        <Collapse in={isExpanded}>
          <Typography sx={{ mt: 2 }}>No unread alerts available.</Typography>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: isSmallScreen ? "column" : "row",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}>
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Alerts
          </Typography>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Paper
          elevation={3}
          sx={{ mb: 3, overflow: "hidden", position: "relative" }}
        >
          {loading && allAlerts.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(13, 17, 23, 0.7)"
                    : "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <TableContainer
            data-testid="alerts-table-container"
            sx={{
              height: {
                xs: 400,
                sm: 500,
                md: 600,
              },
              overflow: "auto",
              "& .MuiTableHead-root": {
                position: "sticky",
                top: 0,
                zIndex: 10,
              },
            }}
          >
            <Table aria-label="alerts table" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align="left"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        position: "relative",
                        width: headCell.width,
                        pl: 2,
                        pr: 3,
                        zIndex: 2,
                        cursor: headCell.sortable ? "pointer" : "default",
                      }}
                      onClick={() =>
                        headCell.sortable &&
                        handleSort(headCell.id as AlertSortField)
                      }
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={localSortConfig.field === headCell.id}
                          direction={
                            localSortConfig.field === headCell.id
                              ? localSortConfig.direction
                              : "asc"
                          }
                        >
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMappedAlerts.map((alert) => (
                  <TableRow
                    key={`${alert.type}-${alert.id}`}
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 152, 0, 0.2)",
                      },
                    }}
                  >
                    <TableCell align="left">{alert.taskNumber === 0 ? "N/A" : alert.taskNumber}</TableCell>
                    <TableCell align="left">
                      <Typography
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.43,
                          letterSpacing: "0.01071em",
                          fontWeight: 400,
                          fontSize: "0.875rem",
                        }}
                      >
                        {alert.message}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">{alert.developers}</TableCell>
                    <TableCell align="left">{alert.timeBudget}</TableCell>
                    <TableCell align="left">
                      {new Date(alert.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={getAlertTypeLabel(alert.type)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Box sx={{ flex: { xs: "1", sm: "1 1 25%" } }}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} ({allAlerts.length} items total)
              </Typography>
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 50%" },
                display: "flex",
                justifyContent: "center",
                order: { xs: 3, sm: 2 },
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isSmallScreen ? "small" : "medium"}
                showFirstButton
                showLastButton
                siblingCount={isSmallScreen ? 0 : 1}
                sx={{
                  "& .MuiPaginationItem-root": {
                    minWidth: { xs: 30, sm: 32 },
                    height: { xs: 30, sm: 32 },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 25%" },
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                order: { xs: 2, sm: 3 },
              }}
            >
              <FormControl
                variant="outlined"
                size="small"
                sx={{ minWidth: { xs: 150, sm: 120 } }}
              >
                <InputLabel id="page-size-select-label">
                  Items per page
                </InputLabel>
                <Select
                  labelId="page-size-select-label"
                  id="page-size-select"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Items per page"
                >
                  {pageSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};
